import React, { Dispatch, useEffect, useState } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { connect, ConnectedProps } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { Action } from 'redux';

import { CurrencySymbolEnum } from '../../lib/enums/currency.enum';
import lang from '../../lib/language';
import { RootState } from '../../lib/store';
import { getAccountsSummary, internalTransfer } from '../../lib/store/crm';
import { CRMCall } from '../../lib/store/crm/types';
import { fixToFloat, formatCurrencyBySymbol } from '../../lib/util/DataHelpers';
import { isCallStatusError, isCallStatusReady } from '../../lib/util/error-handling/StatusByCallHelpers';

import backArrow from '../../assets/img/icon-arrow_back.svg';
import { Button, ClickableImage } from '../../components';
import { useIsMobile } from '../../hooks';
import { UI_ROUTES } from '../../util/routes';

import styles from './InternalTransfer.module.scss';

type Props = {} & ReduxProps;

const InternalTransfer = (props: Props) => {
  const {
    fundPortalAvailableCash,
    getAccSummary,
    transfer,
    transferStatusReady,
    transferStatusError,
  } = props;

  const isMobile = useIsMobile();
  const history = useHistory();
  const location = history.location.state as { target: string, previousPage: string };

  const [withdrawalAmount, setWithdrawalAmount] = useState(0.00);
  const [hasMadeTransfer, setHasMadeTransfer] = useState(false);

  useEffect(() => {
    getAccSummary();
  }, [getAccSummary]);

  useEffect(() => {
    if (hasMadeTransfer && (transferStatusReady || transferStatusError)) {
      history.push({
        pathname: UI_ROUTES.funds,
        state: {
          amount: withdrawalAmount,
          isSuccess: !!transferStatusReady,
        },
      });
    }
  }, [transferStatusReady, transferStatusError, history, withdrawalAmount, hasMadeTransfer, location.target]);

  const handleTransfer = () => {
    transfer(withdrawalAmount);
    setHasMadeTransfer(true);
  };

  const renderTransferButton = () => (
    <div className={styles.withdrawBtnBox}>
      <Button
        fullWidth
        disabled={
          withdrawalAmount === 0
          || isNaN(withdrawalAmount)
          || withdrawalAmount > fixToFloat(fundPortalAvailableCash)
        }
        onClick={handleTransfer}
      >
        {lang.fundScreenTransfer()}
      </Button>
    </div>
  );

  return (
    <>
      <div className={styles.wrapperContainer}>
        <div className={styles.containerContent}>
          <div className={styles.wrapperContent}>
            { !isMobile && (
              <ClickableImage
                alt=""
                src={backArrow}
                className={styles.backArrow}
                onClick={() => history.push(location.previousPage)}
              />
            )}
            <h3 className={styles.title}>{lang.fundScreenInternalTransfer()}</h3>
            <span className={styles.description}>{lang.commonFundPortalMoveFunds()}</span>
            <div className={styles.wrapperFieldAmount}>
              <div className={styles.wrapperAmountText}>
                <div className={styles.currencyInputWrapper}>
                  <div className={styles.firstSection}>
                    <div className={styles.amountText}>
                      {lang.mobileDepositAmountLabel()}
                    </div>
                    <CurrencyInput
                      autoFocus
                      onValueChange={(value) => setWithdrawalAmount(Number(value))}
                      className={classNames(styles.input,
                        { [styles.errorInput]:
                          Number(withdrawalAmount) > fixToFloat(fundPortalAvailableCash) })}
                      placeholder={`${CurrencySymbolEnum.EURO}0.00`}
                      allowNegativeValue={false}
                      prefix={CurrencySymbolEnum.EURO}
                    />
                  </div>
                </div>
                <div className={styles.availableCash}>
                  <span className={classNames(styles.textAvailableCash,
                    { [styles.textAvailableCashError]:
                      withdrawalAmount > fixToFloat(fundPortalAvailableCash) })}
                  >
                    {`${lang.mobileFundsScreenAvailableCash()} ${formatCurrencyBySymbol(fundPortalAvailableCash, CurrencySymbolEnum.EURO)}`}
                  </span>
                </div>
              </div>
            </div>
            { !isMobile && renderTransferButton() }
          </div>
        </div>
      </div>
      { isMobile && renderTransferButton() }
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  fundPortalAvailableCash: state.crm.fundPortalAvailableCash,
  transferStatusReady: isCallStatusReady(CRMCall.internalTransfer, state.crm.statusByCall),
  transferStatusError: isCallStatusError(CRMCall.internalTransfer, state.crm.statusByCall),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getAccSummary: () => dispatch(getAccountsSummary()),
  transfer: (amount: number) => dispatch(internalTransfer({ amount })),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(InternalTransfer);
